import React from "react";
import {CommissionModel} from "../../models/commission";
import Table from 'rc-table';
import {ColumnProps} from "rc-table/lib/sugar/Column";
import {Heading4, Paragraph} from "../text";
import Moment from "moment";
import {CurrencyUtils} from "../../utils/currencyUtils";
import {useTranslation} from "next-i18next";
import {CommissionPayoutModel} from "../../models/commissionPayout";
import {PromoCodeModel} from "../../models/promoCode";
import {Button} from "../buttons/Button";
import {useAppSelector} from "../../app/hooks";
import {selectIsLoadingPromoCodes, selectSelectedTab} from "../../redux/affiliate/affiliateSlice";
import {useDeeplinkRouter} from "../../utils/useDeeplinkRouter";

export interface AffiliateDashboardTableProps {
  commissions: CommissionModel[]
  cashPayouts: CommissionPayoutModel[]
  storeCreditPayouts: CommissionPayoutModel[]
  promoCodes: PromoCodeModel[]
  promoCodePageNumber: number
  onLoadMorePromoCodesClicked: () => void
}

const TableHeader: React.FC<{ isFirst?: boolean, isLast?: boolean }> = ({ isFirst, children, isLast }) => {
  return <div className={`py-3 px-4 whitespace-nowrap flex items-center bg-fg-gray-surface-light h-12 ${isFirst ? 'rounded-tl-lg' : isLast ? 'rounded-tr-lg' : ''}`}>
    {children}
  </div>
}

const TableCell: React.FC<any> = ({ children }) => {
  return <div className={`py-3 px-4 whitespace-nowrap border-t border-fg-gray-surface h-16 flex items-center`}>
    {children}
  </div>
}

export const AffiliateDashboardTable: React.FC<AffiliateDashboardTableProps> = ({ commissions, cashPayouts, storeCreditPayouts, promoCodes, onLoadMorePromoCodesClicked, promoCodePageNumber }) => {
  const { t } = useTranslation()
  const tab = useAppSelector(selectSelectedTab)
  const isLoadingPromoCodes = useAppSelector(selectIsLoadingPromoCodes)
  const deepLinkRouter = useDeeplinkRouter()

  const commissionColumns: ColumnProps<CommissionModel>[] = [
    {
      title: <TableHeader isFirst={true}>
        <Heading4 className="text-fg-gray-dark">{t('affiliate:affiliate_title_credited_on')}</Heading4>
      </TableHeader>,
      dataIndex: 'credited_on',
      onHeaderCell: () => {
        return {
          onClick: () => {

          }
        }
      },
      render: (text, record) => (
        <TableCell>
          <Paragraph>{Moment(record.createdAt).format('MMM D, YYYY')}</Paragraph>
        </TableCell>
      )
    },
    {
      title: <TableHeader>
        <Heading4 className="text-fg-gray-dark">{t('affiliate:affiliate_title_order_subtotal')}</Heading4>
      </TableHeader>,
      dataIndex: 'subtotal',
      onHeaderCell: () => {
        return {
          onClick: () => {

          }
        }
      },
      render: (text, record) => (
        <TableCell>
          <Paragraph>{CurrencyUtils.stringFromPrice(record.order?.paymentSummary?.subtotal, record.order?.paymentSummary?.currencyCode)}</Paragraph>
        </TableCell>
      )
    },
    {
      title: <TableHeader>
        <Heading4 className="text-fg-gray-dark">{t('affiliate:affiliate_title_commission_amount')}</Heading4>
      </TableHeader>,
      dataIndex: 'commission_amount',
      onHeaderCell: () => {
        return {
          onClick: () => {

          }
        }
      },
      render: (text, record) => (
        <TableCell>
          <Paragraph>{CurrencyUtils.stringFromPrice(record.amount, record.currencyCode)}</Paragraph>
        </TableCell>
      )
    },
    {
      title: <TableHeader isLast={true}>
        <Heading4 className="text-fg-gray-dark">{t('affiliate:affiliate_title_status')}</Heading4>
      </TableHeader>,
      dataIndex: 'status',
      onHeaderCell: () => {
        return {
          onClick: () => {

          }
        }
      },
      render: (text, record) => (
        <TableCell>
          <Paragraph className="capitalize">{record.status}</Paragraph>
        </TableCell>
      )
    },
  ]

  const storeCreditPayoutColumns: ColumnProps<CommissionPayoutModel>[] = [
    {
      title: <TableHeader isFirst={true}>
        <Heading4 className="text-fg-gray-dark">{t('affiliate:affiliate_title_paid_on')}</Heading4>
      </TableHeader>,
      dataIndex: 'paid_on',
      onHeaderCell: () => {
        return {
          onClick: () => {

          }
        }
      },
      render: (text, record) => (
        <TableCell>
          <Paragraph>{Moment(record.createdAt).format('MMM D, YYYY')}</Paragraph>
        </TableCell>
      )
    },
    {
      title: <TableHeader>
        <Heading4 className="text-fg-gray-dark text-left">{t('affiliate:affiliate_title_store')}</Heading4>
      </TableHeader>,
      dataIndex: 'store',
      onHeaderCell: () => {
        return {
          onClick: () => {

          }
        }
      },
      render: (text, record, index) => (
        <TableCell>
          <Paragraph>{record.vendor?.name}</Paragraph>
        </TableCell>
      )
    },
    {
      title: <TableHeader>
        <Heading4 className="text-fg-gray-dark">{t('affiliate:affiliate_title_amount')}</Heading4>
      </TableHeader>,
      dataIndex: 'amount',
      onHeaderCell: () => {
        return {
          onClick: () => {

          }
        }
      },
      render: (text, record) => (
        <TableCell>
          <Paragraph>{CurrencyUtils.stringFromPrice(record.amount, record.currencyCode)}</Paragraph>
        </TableCell>
      )
    },
    {
      title: <TableHeader isLast={true}>
        <Heading4 className="text-fg-gray-dark">{t('affiliate:affiliate_title_payout_type')}</Heading4>
      </TableHeader>,
      dataIndex: 'type',
      onHeaderCell: () => {
        return {
          onClick: () => {

          }
        }
      },
      render: (text, record) => (
        <TableCell>
          <Paragraph>{record.type === 'cash' ? t('affiliate:affiliate_title_cash') : t('affiliate:affiliate_title_store_credit')}</Paragraph>
        </TableCell>
      )
    },
  ]

  const cashPayoutColumns: ColumnProps<CommissionPayoutModel>[] = [
    {
      title: <TableHeader isFirst={true}>
        <Heading4 className="text-fg-gray-dark">{t('affiliate:affiliate_title_paid_on')}</Heading4>
      </TableHeader>,
      dataIndex: 'paid_on',
      onHeaderCell: () => {
        return {
          onClick: () => {

          }
        }
      },
      render: (text, record) => (
        <TableCell>
          <Paragraph>{Moment(record.createdAt).format('MMM D, YYYY')}</Paragraph>
        </TableCell>
      )
    },
    {
      title: <TableHeader>
        <Heading4 className="text-fg-gray-dark">{t('affiliate:affiliate_title_amount')}</Heading4>
      </TableHeader>,
      dataIndex: 'amount',
      onHeaderCell: () => {
        return {
          onClick: () => {

          }
        }
      },
      render: (text, record) => (
        <TableCell>
          <Paragraph>{CurrencyUtils.stringFromPrice(record.amount, record.currencyCode)}</Paragraph>
        </TableCell>
      )
    },
    {
      title: <TableHeader isLast={true}>
        <Heading4 className="text-fg-gray-dark">{t('affiliate:affiliate_title_payout_type')}</Heading4>
      </TableHeader>,
      dataIndex: 'type',
      onHeaderCell: () => {
        return {
          onClick: () => {

          }
        }
      },
      render: (text, record) => (
        <TableCell>
          <Paragraph>{record.type === 'cash' ? t('affiliate:affiliate_title_cash') : t('affiliate:affiliate_title_store_credit')}</Paragraph>
        </TableCell>
      )
    },
  ]

  const promoCodeColumns: ColumnProps<PromoCodeModel>[] = [
    {
      title: <TableHeader isFirst={true}>
        <Heading4 className="text-fg-gray-dark text-left">{t('affiliate:affiliate_title_details')}</Heading4>
      </TableHeader>,
      dataIndex: 'details',
      onHeaderCell: () => {
        return {
          onClick: () => {

          }
        }
      },
      render: (text, record, index) => (
        <TableCell>
          <Paragraph>{record.title}</Paragraph>
        </TableCell>
      )
    },
    {
      title: <TableHeader>
        <Heading4 className="text-fg-gray-dark text-left">{t('affiliate:affiliate_title_store')}</Heading4>
      </TableHeader>,
      dataIndex: 'store',
      onHeaderCell: () => {
        return {
          onClick: () => {

          }
        }
      },
      render: (text, record, index) => (
        <TableCell>
          <Paragraph>{record.vendors[0]?.name}</Paragraph>
        </TableCell>
      )
    },
    {
      title: <TableHeader>
        <Heading4 className="text-fg-gray-dark text-left">{t('affiliate:affiliate_title_code')}</Heading4>
      </TableHeader>,
      dataIndex: 'code',
      onHeaderCell: () => {
        return {
          onClick: () => {

          }
        }
      },
      render: (text, record, index) => (
        <TableCell>
          <Paragraph>{record.code}</Paragraph>
        </TableCell>
      )
    },
    {
      title: <TableHeader isLast={true}>
      </TableHeader>,
      dataIndex: 'action',
      onHeaderCell: () => {
        return {
          onClick: () => {

          }
        }
      },
      render: (text, record, index) => (
        <TableCell>
          <Button
            buttonStyle="text_primary"
            onClick={() => {
              const url = new URL(`fitgenie://store?store_id=${record.vendors[0]?.id}&promo_code=${record.id}`)
              deepLinkRouter.handleDeeplinkUrl(url)
            }}
          >
            {t('affiliate:affiliate_title_use_this_promo')}
          </Button>
        </TableCell>
      )
    },
  ]

  switch (tab) {
    case "commissions":
      return <Table
        className="rounded-lg border border-fg-gray-surface fg-table"
        scroll={{x: true}}
        emptyText={() => {
          return <div className="w-full h-16 flex items-center justify-center">
            <Paragraph>{t('affiliate:affiliate_empty_state_title_no_commissions')}</Paragraph>
          </div>
        }}
        columns={commissionColumns}
        data={commissions}
      />
    case "cash_payouts":
      return <Table
        className="rounded-lg border border-fg-gray-surface fg-table"
        scroll={{x: true}}
        emptyText={() => {
          return <div className="w-full h-16 flex items-center justify-center">
            <Paragraph>{t('affiliate:affiliate_empty_state_title_no_payouts')}</Paragraph>
          </div>
        }}
        columns={cashPayoutColumns}
        data={cashPayouts}
      />
    case "store_credit_payouts":
      return <Table
        className="rounded-lg border border-fg-gray-surface fg-table"
        scroll={{x: true}}
        emptyText={() => {
          return <div className="w-full h-16 flex items-center justify-center">
            <Paragraph>{t('affiliate:affiliate_empty_state_title_no_store_credits')}</Paragraph>
          </div>
        }}
        columns={storeCreditPayoutColumns}
        data={storeCreditPayouts}
      />
    case "perks":
      return <Table
        className="rounded-lg border border-fg-gray-surface fg-table"
        scroll={{x: true}}
        emptyText={() => {
          return <div className="w-full h-16 flex items-center justify-center">
            <Paragraph>{t('affiliate:affiliate_empty_state_title_no_perks')}</Paragraph>
          </div>
        }}
        columns={promoCodeColumns}
        data={promoCodes}
        footer={() => {
          return <div className="w-full flex items-center justify-center py-8 border-t border-fg-gray-surface">
            <Button
              buttonStyle="secondary"
              isLoading={isLoadingPromoCodes}
              onClick={() => {
                onLoadMorePromoCodesClicked()
              }}
            >
              Load More
            </Button>
          </div>
        }}
      />
  }
}